import { LazyPromise } from '@thoughtspot/blink-context';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import { getIsUserAuthenticated } from '/@services/system/login-service';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import {
    canAccessAnalystStudio,
    canAccessSpotterBenchmark,
    canAdministerSchedules,
    canCreateAnswers,
    canCreateOrEditConnections,
    canManageCustomCalendars,
    canManageWorksheetViewsTables,
    canShowTeamsTab,
    getIsSpotterFullExperienceEnabled,
    getSubscriptionType,
    getUserExpirationTime,
    getUserName,
    getUserState,
    hasDataManagementPrivileges,
    hasDownloadDetailedDataPrivileges,
    hasDownloadVisualPrivileges,
    hasUserDataUploadPrivileges,
    isA3Enabled,
    isAdminTabVisible,
    isAnalystStudioEnabled,
    isEdgeEnabled,
    isEurekaEnabled,
    isEverywhereEnabled,
    isLoggedIn,
    isMonitorV2Enabled,
    isScheduleOnHomeEnabled,
    isSparkFlowEnabled,
    isSplitDownloadPrivilegesEnabled,
    shouldDisplayAIControls,
    shouldHideDataTabOnNoManageDataPermission,
    SubscriptionType,
    UserState,
} from '../services/system/config-service/session-service';

export enum PrivilegeTypes {
    CopilotFreeTrialExpired = 'CopilotFreeTrialExpired',
    CopilotFreeTrialNotExpired = 'CopilotFreeTrialNotExpired',
    isSpotterFullExperienceEnabled = 'isSpotterFullExperienceEnabled',
    MustBeAuthenticated = 'MustBeAuthenticated',
    MustNotBeAuthenticated = 'MustNotBeAuthenticated',
    CanSeeEurekaSearch = 'CanSeeEurekaSearch',
    CanSeeAIAnswer = 'CanSeeAIAnswer',
    CanAccessMonitor = 'CanAccessMonitor',
    CanAccessLiveboardSchedulesOnHome = 'CanAccessLiveboardSchedulesOnHome',
    HasDeveloperAccess = 'HasDeveloperAccess',
    HasAdminAccess = 'HasAdminAccess',
    CanAccessA3 = 'CanAccessA3',
    CanAccessSpotterBenchmark = 'CanAccessSpotterBenchmark',
    CanAccessDataTab = 'CanAccessDataTab',
    HasAccessToCopilot = 'HasAccessToCopilot',
    CanAccessAnalystStudio = 'CanAccessAnalystStudio',
    CanSeeEdgeOnboarding = 'CanSeeEdgeOnboarding',
    canManageWorksheetViewsTables = 'CanManageWorksheetViewsTables',
    IsExpiredUser = 'IsExpiredUser',
    CanAccessLiveboardSchedules = 'CanAccessLiveboardSchedules',
    HasTeamsAccess = 'HasTeamsAccess',
    CanSeeSparkflowOnboarding = 'CanSeeSparkflowOnboarding',
    IsNotEmbeddedApp = 'IsNotEmbeddedApp',
    CanCreateAnswers = 'CanCreateAnswers',
}

type Privileges = {
    [key in PrivilegeTypes]?: LazyPromise<void>;
};

const MustBeAuthenticated = new LazyPromise<void>((resolve, reject) => {
    return getIsUserAuthenticated()
        .then(isAuthenticated => (isAuthenticated ? resolve() : reject()))
        .catch(reject);
});

const MustNotBeAuthenticated = new LazyPromise<void>((resolve, reject) => {
    return getIsUserAuthenticated()
        .then(isAuthenticated => (isAuthenticated ? reject() : resolve()))
        .catch(() => resolve());
});

const getAuthenticatedSessionCheck = (
    booleanFlagCheck: () => boolean,
): LazyPromise<void> => {
    return new LazyPromise<void>((resolve, reject) => {
        // For V1 - When authentication is not handled by V2
        if (!!isLoggedIn && isLoggedIn()) {
            if (booleanFlagCheck()) {
                return resolve();
            }
            return reject();
        }
        return MustBeAuthenticated.then(() => {
            if (booleanFlagCheck()) {
                return resolve();
            }
            return reject();
        }).catch(reject);
    });
};

const IsCopilotFreeTrialUserExpired = () => {
    if (getSubscriptionType() !== SubscriptionType.FREE_TRIAL) {
        return false;
    }
    const expiryEpochTime = getUserExpirationTime();
    const currentEpochTime = Date.now();
    const isUserExpired = expiryEpochTime - currentEpochTime;
    return isUserExpired <= 0;
};

const getIsCopilotFreeTrialExpired = () => {
    return getAuthenticatedSessionCheck(() => IsCopilotFreeTrialUserExpired());
};

const getIsCopilotFreeTrialNotExpired = () => {
    return getAuthenticatedSessionCheck(() => !IsCopilotFreeTrialUserExpired());
};

const getIsEdgeEnabled = () => {
    return getAuthenticatedSessionCheck(() => isEdgeEnabled());
};

const getIsSparkflowEnabled = () => {
    return getAuthenticatedSessionCheck(() => isSparkFlowEnabled());
};
const isSpotterFullExperienceEnabled = () => {
    return getAuthenticatedSessionCheck(
        () =>
            getIsSpotterFullExperienceEnabled() || getUserName() === 'tsadmin',
    );
};

export const hasDataTabAccessPrivileges = () =>
    hasDataManagementPrivileges() ||
    hasUserDataUploadPrivileges() ||
    canManageCustomCalendars() ||
    canCreateOrEditConnections() ||
    canManageWorksheetViewsTables() ||
    !shouldHideDataTabOnNoManageDataPermission();

const hasDataTabAccess = () => {
    return getAuthenticatedSessionCheck(() => hasDataTabAccessPrivileges());
};

const canManageWorksheetViewsTablesPrivileges = () => {
    return getAuthenticatedSessionCheck(() => canManageWorksheetViewsTables());
};

const getIsExpiredUser = () => {
    return getAuthenticatedSessionCheck(
        () => getUserState() === UserState.EXPIRED,
    );
};

const hasCopilotAccess = () => {
    return getAuthenticatedSessionCheck(
        () => isFeatureEnabled(FEATURE_FLAG_NAMES.COPILOT_ENABLED) as boolean,
    );
};

const hasAnalystStudioAccess = () => {
    return getAuthenticatedSessionCheck(
        () =>
            isAnalystStudioEnabled() &&
            isOktaEnabled() &&
            canAccessAnalystStudio(),
    );
};

export const isSpotterBenchmarkAccessible = (): boolean =>
    canAccessSpotterBenchmark();

export const hasSchedulePageAccessPrivilege = () => {
    if (canAdministerSchedules()) {
        return true;
    }
    return isSplitDownloadPrivilegesEnabled()
        ? hasDownloadDetailedDataPrivileges() || hasDownloadVisualPrivileges()
        : true;
};

const hasAccessToLiveboardSchedulesOnHome = () => {
    return getAuthenticatedSessionCheck(
        () => isScheduleOnHomeEnabled() && hasSchedulePageAccessPrivilege(),
    );
};

const hasAccessToLiveboardSchedules = () => {
    return getAuthenticatedSessionCheck(() => hasSchedulePageAccessPrivilege());
};

const IsNotEmbeddedApp = new LazyPromise<void>((resolve, reject) => {
    return isEmbeddedApp() ? reject() : resolve();
});

// Flag-aware and available-by-default: rejects only on explicit revocation.
const hasCreateAnswersAccess = () => {
    return getAuthenticatedSessionCheck(() => canCreateAnswers());
};

// DO NOT CHANGE WITHOUT FE INFRA APPROVAL
export const BASE_PRIVILEGES: Privileges = {
    MustBeAuthenticated,
    MustNotBeAuthenticated,
};

/**
 * A privilege is a lazy promise that resolves to indicate authorization
 * Add new privileges under the relevant section
 */
export const PrivilegeList: Privileges = {
    ...BASE_PRIVILEGES,
    // INSIGHTS
    CanSeeEurekaSearch: getAuthenticatedSessionCheck(isEurekaEnabled),
    CanSeeAIAnswer: getAuthenticatedSessionCheck(shouldDisplayAIControls),
    CanAccessMonitor: getAuthenticatedSessionCheck(isMonitorV2Enabled),
    CanAccessA3: getAuthenticatedSessionCheck(isA3Enabled),
    CanAccessSpotterBenchmark: getAuthenticatedSessionCheck(
        isSpotterBenchmarkAccessible,
    ),
    CanAccessLiveboardSchedulesOnHome: hasAccessToLiveboardSchedulesOnHome(),
    CanAccessLiveboardSchedules: hasAccessToLiveboardSchedules(),
    CanCreateAnswers: hasCreateAnswersAccess(),
    // DEVELOP
    HasDeveloperAccess: getAuthenticatedSessionCheck(isEverywhereEnabled),
    // TEAMS_TAB IS USED FOR ALL MY* PRODUCTION CLUSTERS
    HasTeamsAccess: getAuthenticatedSessionCheck(canShowTeamsTab),
    // ADMIN
    HasAdminAccess: getAuthenticatedSessionCheck(isAdminTabVisible),
    IsNotEmbeddedApp,
    // DATA
    CanAccessDataTab: hasDataTabAccess(),
    // COPILOT
    HasAccessToCopilot: hasCopilotAccess(),
    CopilotFreeTrialNotExpired: getIsCopilotFreeTrialNotExpired(),
    CopilotFreeTrialExpired: getIsCopilotFreeTrialExpired(),
    isSpotterFullExperienceEnabled: isSpotterFullExperienceEnabled(),
    // ANALYST STUDIO ACCESS
    CanAccessAnalystStudio: hasAnalystStudioAccess(),
    // EDGE
    CanSeeEdgeOnboarding: getIsEdgeEnabled(),
    // SPARKFLOW
    CanSeeSparkflowOnboarding: getIsSparkflowEnabled(),
    // Can Manage worksheet and tables
    CanManageWorksheetViewsTables: canManageWorksheetViewsTablesPrivileges(),
    // IsExpiredUser
    IsExpiredUser: getIsExpiredUser(),
};

export const resetAllPrivileges = () => {
    Object.keys(PrivilegeList).forEach((privilege: PrivilegeTypes) => {
        PrivilegeList[privilege].reset();
    });
};
